import React, { createContext, useContext, useEffect, useLayoutEffect, useState } from "react";
import { RIF } from "../../lib";
import { useSelectTheme } from "../../store";
import type { QuestionnaireDraftInputBuffer } from "../../model/questionnaire_draft";

export enum QuestionnaireInputSize {
  extraSmall = "72px",
  verySmall = "340px",
  small = "36.286rem",
  medium = "38.143rem",
  big = "53.143rem",
  displayLogic = "120px",
  displayLogicBetween = "88px",
}

export enum QuestionnaireInputHeight {
  medium = "40px",
  big = "48px",
}

// The questionnaire editor's record of input text the parent does not hold yet: an emptied field, or a
// partial number such as "-" or "12.", is only committed on blur, if at all.
export interface QuestionnaireInputDraftRegistry {
  report: (field: string, text: string | null) => void;
  peekRestoredText: (field: string) => string | undefined;
  consumeRestoredText: (field: string) => void;
  // includes restored text whose input has not mounted yet
  readBufferList: () => QuestionnaireDraftInputBuffer[];
}

export const QuestionnaireInputDraftContext = createContext<QuestionnaireInputDraftRegistry | null>(
  null,
);

export const createQuestionnaireInputDraftRegistry = (
  restoredBufferList: QuestionnaireDraftInputBuffer[],
  onChange?: () => void,
): QuestionnaireInputDraftRegistry => {
  const bufferMap = new Map(restoredBufferList.map(({ field, text }) => [field, text]));
  const restoredTextMap = new Map(bufferMap);
  return {
    report: (field, text) => {
      if (text === null ? !bufferMap.delete(field) : bufferMap.get(field) === text) return;
      if (text !== null) bufferMap.set(field, text);
      onChange?.();
    },
    peekRestoredText: (field) => restoredTextMap.get(field),
    consumeRestoredText: (field) => {
      restoredTextMap.delete(field);
    },
    readBufferList: () => Array.from(bufferMap, ([field, text]) => ({ field, text })),
  };
};

export const QuestionnaireInput = (props: {
  // identifies this input in a questionnaire draft; without it the input takes no part in drafts
  draftField?: string;
  defaultValue?: string;
  initialValue?: string;
  onChangeAction?: (arg: string) => void;
  placeholder: string;
  maxLength: number;
  size?: QuestionnaireInputSize;
  disabled?: boolean;
  inputType?: "number" | "integer" | "text";
  hideWordCount?: boolean;
  questionnaireInputHeight?: QuestionnaireInputHeight;
}) => {
  const { color, fontWeight, fontSize } = useSelectTheme();
  const {
    draftField,
    defaultValue,
    initialValue,
    placeholder,
    onChangeAction,
    maxLength,
    size,
    disabled,
    inputType,
    hideWordCount,
    questionnaireInputHeight,
  } = props;
  const [focusing, setFocusing] = useState(false);
  const draftRegistry = useContext(QuestionnaireInputDraftContext);
  const [inputValue, setInputValue] = useState(
    () =>
      (draftField === undefined ? undefined : draftRegistry?.peekRestoredText(draftField)) ??
      defaultValue ??
      "",
  );

  useEffect(() => {
    if (draftField !== undefined) draftRegistry?.consumeRestoredText(draftField);
  }, []);

  // reported after commit, so whoever the registry notifies reads the text that is on screen
  useLayoutEffect(() => {
    if (draftField === undefined || !draftRegistry) return;
    draftRegistry.report(draftField, inputValue === (defaultValue ?? "") ? null : inputValue);
  }, [draftRegistry, draftField, inputValue, defaultValue]);

  useLayoutEffect(() => {
    if (draftField === undefined || !draftRegistry) return;
    return () => draftRegistry.report(draftField, null);
  }, [draftRegistry, draftField]);

  const fontSizeMap = {
    [QuestionnaireInputHeight.medium]: fontSize.h6,
    [QuestionnaireInputHeight.big]: fontSize.h3,
  };

  const handleFocus = () => {
    setFocusing(true);
    if (inputValue === initialValue) {
      setInputValue("");
    }
  };

  const handleBlur = () => {
    setFocusing(false);
    if (inputValue === "") {
      setInputValue(initialValue ?? "");
      if (onChangeAction) onChangeAction(initialValue ?? "");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value === "" || e.target.value === "-") {
      setInputValue(e.target.value);
    } else {
      if (inputType === "number") {
        if (e.target.value === "") {
          setInputValue("0");
        } else {
          setInputValue(
            e.target.value
              .replace(/[^0-9.-]/g, "")
              .replace(/(?!^)-/g, "")
              .replace(/(\..*)\./g, "$1"),
          );
        }
      } else if (inputType === "integer") {
        if (e.target.value === "") {
          setInputValue("0");
        } else {
          setInputValue(e.target.value.replace(/[^0-9-]/g, "").replace(/(?!^)-/g, ""));
        }
      } else {
        setInputValue(e.target.value);
      }
      if (onChangeAction) onChangeAction(e.target.value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (onChangeAction) onChangeAction(inputValue);
    }
  };

  return (
    <div
      // {...props}
      css={{
        width: size ?? "100%",
        height: questionnaireInputHeight ?? QuestionnaireInputHeight.medium,
        minHeight: questionnaireInputHeight ?? QuestionnaireInputHeight.medium,
        position: "relative",
        borderRadius: "5px",
        overflow: "hidden",
        border: `1px solid ${disabled ? color.border.default : focusing ? color.grey_400 : color.border.default}`,
        ":hover": {
          border: `1px solid ${disabled ? color.border.default : color.grey_400}`,
        },
      }}
    >
      <textarea
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        value={inputValue}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        // rows={1}
        css={{
          border: "none",
          width: "100%",
          lineHeight: "18px",
          height: "100%",
          margin: 0,
          padding: `11px ${hideWordCount ? "0" : "110px"} 0 20px`,
          backgroundColor: disabled ? color.surface.default : color.white,
          color: disabled ? color.state.disabled : color.textIcon.title,
          paddingLeft: "16px",
          fontSize: fontSizeMap[questionnaireInputHeight ?? QuestionnaireInputHeight.medium],
          resize: "none",
          boxSizing: "border-box",
          overflowY: "auto",
          // minHeight: '100%',
          // backgroundColor: color.white,
          ":focus": {
            outline: "none",
          },
        }}
      />
      {RIF(
        focusing && !hideWordCount,
        <p
          css={{
            color: color.grey_400,
            fontWeight: fontWeight.medium,
            height: "100%",
            display: "flex",
            alignItems: "center",
            position: "absolute",
            right: "20px",
            top: "0",
          }}
        >
          {inputValue.length} / {maxLength}
        </p>,
      )}
    </div>
  );
};
